// Customer portal — Reviews & Referrals section.
//
// Two stacked blocks inside one card:
//   1. Google review prompt — renders only when the project is in a
//      "review-worthy" stage AND the retailer has a googleReviewUrl.
//      Sticky-dismissed via localStorage; click-through reported best-
//      effort to /customer/review-prompt/clicked.
//   2. Refer-a-friend — always renders (no source-of-truth equivalent;
//      added 2026-05-19). Shows the customer their personal referral
//      link (placeholder format `https://thekitchenos.io/r/<customer.id>`)
//      with a "Copy link" button (clipboard with execCommand fallback)
//      and a "Send via Messages" button that scrolls to the in-portal
//      Messages section. Platform-only — never email/SMS.
//      TODO backend: GET/POST /api/customer/referrals/link — for now
//      we mint the link client-side from customer.id.
//
// Section visibility:
//   - Shown when EITHER the review block OR the referrals block has
//     something to show. Review-only or referrals-only is fine.

import { ApiError } from "@tkos/api-client";

import type {
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./review-prompt.css";

const ELIGIBLE_STATUSES = new Set<CustomerProject["status"]>([
  "delivered",
  "installed",
  "complete",
]);

function dismissKey(retailerId: string, customerId: string): string {
  return `tkos.review-prompt.dismissed.${retailerId}.${customerId}`;
}

function isDismissedLocally(retailerId: string, customerId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(retailerId, customerId)) === "1";
  } catch {
    return false;
  }
}

function markDismissedLocally(retailerId: string, customerId: string): void {
  try {
    localStorage.setItem(dismissKey(retailerId, customerId), "1");
  } catch {
    /* ignore — private mode etc. */
  }
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Refer-a-friend is hidden until the referral backend ships. The placeholder
// link below resolves nowhere on any host, so rendering the block hands real
// customers a "Copy link" button for a dead URL. Flip to true once
// GET /api/customer/referrals/link exists and a destination is decided.
const REFERRALS_ENABLED: boolean = false;

function buildReferralLink(customerId: string): string {
  // Placeholder link format until the backend referral endpoint ships.
  // TODO: GET /api/customer/referrals/link — server-signed token so we
  // can attribute conversions correctly.
  return `https://thekitchenos.io/r/${encodeURIComponent(customerId)}`;
}

function isReviewBlockEligible(project: CustomerProject): boolean {
  if (!ELIGIBLE_STATUSES.has(project.status)) return false;
  if (!project.retailer.googleReviewUrl) return false;
  if (isDismissedLocally(project.retailer.id, project.customer.id)) return false;
  return true;
}

// Clipboard helper — mirrors tracking.ts:187-209 (modern API with
// execCommand fallback for older Safari).
async function copyText(text: string): Promise<boolean> {
  const trimmed = text.trim();
  if (!trimmed) return false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(trimmed);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = trimmed;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// One interactive 1–5 star button. Custom inline SVG (1.5px stroke, rounded)
// per the design rules — no icon library, no emoji.
function starBtnHtml(n: number): string {
  return `<button type="button" class="rv-rate-star" data-star="${n}" role="radio" aria-checked="false" aria-label="${n} star${n > 1 ? "s" : ""}">
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 3.25l2.7 5.46 6.03.88-4.36 4.25 1.03 6L12 17.02 6.57 19.84l1.03-6L3.24 9.59l6.03-.88z"/>
    </svg>
  </button>`;
}

// Star-rating block — the customer rates their retailer experience (saved to
// customer_ratings, the V3 marketplace foundation). Always shown.
function ratingBlockHtml(name: string): string {
  return `
    <div class="rv-block rv-rate" data-role="rate-block">
      <h3 class="rv-subtitle">How was your experience with ${esc(name)}?</h3>
      <div class="rv-rate-stars" data-role="rate-stars" role="radiogroup" aria-label="Your star rating">
        ${[1, 2, 3, 4, 5].map(starBtnHtml).join("")}
      </div>
      <textarea class="rv-rate-comment" data-role="rate-comment" rows="2"
        placeholder="Anything you'd like to add? (optional)" maxlength="2000"></textarea>
      <div class="rv-actions">
        <button type="button" class="cust-btn rv-cta" data-role="rate-submit" disabled>Submit rating</button>
        <span class="rv-rate-status" data-role="rate-status" aria-live="polite"></span>
      </div>
    </div>`;
}

function renderCardHtml(project: CustomerProject): string {
  const name = project.retailer.businessName || "your retailer";
  const customerFirst = project.customer.firstName?.trim() || "";
  const greeting = customerFirst ? `Hi ${esc(customerFirst)}!` : "Reviews &amp; referrals";
  const showReview = isReviewBlockEligible(project);

  const reviewBlock = showReview
    ? `
      <div class="rv-block" data-role="review-block">
        <div class="rv-stars" aria-hidden="true">★ ★ ★ ★ ★</div>
        <p class="rv-body">
          If you're happy with your kitchen, a quick Google review would mean the
          world to ${esc(name)} — independents like ours live and die by word of mouth.
          It takes about a minute.
        </p>
        <div class="rv-actions">
          <button type="button" class="cust-btn rv-cta" data-role="review-go">
            Leave a Google review
          </button>
          <button type="button" class="cust-btn cust-btn-secondary rv-dismiss" data-role="review-dismiss">
            Maybe later
          </button>
        </div>
      </div>
    `
    : "";

  const referralBlock = REFERRALS_ENABLED
    ? `
      <div class="rv-block rv-block--refer" data-role="referral-block">
        <h3 class="rv-subtitle">Know someone planning a new kitchen?</h3>
        <p class="rv-body">
          Share your personal referral link. When a friend gets in touch we'll
          both know it was through you.
        </p>
        <div class="rv-refer-row">
          <input class="rv-refer-link" type="text" readonly
                 value="${esc(buildReferralLink(project.customer.id))}"
                 data-role="referral-link"
                 aria-label="Your referral link">
          <button type="button" class="cust-btn rv-copy-btn" data-role="referral-copy">
            Copy link
          </button>
        </div>
        <div class="rv-actions rv-refer-actions">
          <button type="button" class="cust-btn cust-btn-secondary" data-role="referral-send">
            Send via Messages
          </button>
        </div>
      </div>
    `
    : "";

  return `
    <section class="cust-card rv-card" aria-label="Reviews and referrals">
      <h2 class="cust-card-title rv-title">${greeting}</h2>
      ${ratingBlockHtml(name)}
      ${reviewBlock}
      ${referralBlock}
    </section>
  `;
}

const section: CustomerSection = {
  id: "review-prompt",
  // No label — this card is a self-contained CTA, no section heading needed.

  shouldRender(project: CustomerProject): boolean {
    // Show the section if EITHER the review block is eligible OR we have a
    // customer at all — the star-rating block always renders. (Referrals no
    // longer keep the section alive; see REFERRALS_ENABLED.)
    if (isReviewBlockEligible(project)) return true;
    return Boolean(project.customer.id);
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    container.innerHTML = renderCardHtml(ctx.project);

    // ── Star rating state + helpers ───────────────────────────────
    let selectedStars = 0;
    const paintStars = (n: number): void => {
      for (const el of Array.from(container.querySelectorAll<HTMLButtonElement>("[data-star]"))) {
        const v = Number(el.dataset["star"] || 0);
        el.classList.toggle("rv-rate-star--on", v <= n);
        el.setAttribute("aria-checked", String(v === n));
      }
    };
    const setRateStatus = (msg: string): void => {
      const el = container.querySelector<HTMLElement>("[data-role='rate-status']");
      if (el) el.textContent = msg;
    };
    const rateSubmitEl = (): HTMLButtonElement | null =>
      container.querySelector<HTMLButtonElement>("[data-role='rate-submit']");

    // Pre-fill from any rating the customer already left.
    void (async () => {
      try {
        const r = await ctx.api<{ ok: boolean; rating?: { stars: number; comment?: string } | null }>(
          "/customer/rating", { signal: ac.signal });
        if (r && r.rating && r.rating.stars) {
          selectedStars = r.rating.stars;
          paintStars(selectedStars);
          const c = container.querySelector<HTMLTextAreaElement>("[data-role='rate-comment']");
          if (c && r.rating.comment) c.value = r.rating.comment;
          const s = rateSubmitEl(); if (s) s.disabled = false;
          setRateStatus("You've rated this — update any time.");
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return;
        /* silent — rating is non-critical */
      }
    })();

    const url = ctx.project.retailer.googleReviewUrl ?? "";

    function reportEvent(kind: "clicked" | "dismissed"): void {
      // Best-effort — don't block the user's action on this.
      void (async () => {
        try {
          await ctx.api<unknown>("/customer/review-prompt/" + kind, {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({ retailerId: ctx.project.retailer.id }),
          });
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) return;
          // Other errors — silent (UX should not be interrupted).
          console.warn(`[review-prompt] could not report ${kind}`, err);
        }
      })();
    }

    function hideReviewBlock(): void {
      const block = container.querySelector<HTMLElement>("[data-role='review-block']");
      if (!block) return;
      block.style.transition = "opacity 0.25s ease, transform 0.25s ease";
      block.style.opacity = "0";
      block.style.transform = "translateY(-4px)";
      setTimeout(() => block.remove(), 280);
    }

    function onClick(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // ── Star rating ──────────────────────────────────────────────
      const starBtn = target.closest<HTMLButtonElement>("[data-star]");
      if (starBtn) {
        selectedStars = Number(starBtn.dataset["star"] || 0);
        paintStars(selectedStars);
        const s = rateSubmitEl(); if (s) s.disabled = selectedStars < 1;
        setRateStatus("");
        return;
      }
      const rateSubmitBtn = target.closest<HTMLButtonElement>("[data-role='rate-submit']");
      if (rateSubmitBtn) {
        if (selectedStars < 1) return;
        const comment = (container.querySelector<HTMLTextAreaElement>("[data-role='rate-comment']")?.value || "").trim();
        rateSubmitBtn.disabled = true;
        setRateStatus("Saving…");
        void (async () => {
          try {
            await ctx.api<unknown>("/customer/rating", {
              method: "POST",
              signal: ac.signal,
              body: JSON.stringify({ stars: selectedStars, comment }),
            });
            setRateStatus("Thank you — your rating has been saved.");
            ctx.showToast("Thanks for rating your experience!", "success");
            if (rateSubmitEl()) rateSubmitEl()!.disabled = false;
          } catch (err) {
            setRateStatus("Couldn't save your rating — please try again.");
            rateSubmitBtn.disabled = false;
            console.warn("[review-prompt] rating submit failed", err);
          }
        })();
        return;
      }

      const goBtn = target.closest<HTMLButtonElement>("[data-role='review-go']");
      if (goBtn) {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        reportEvent("clicked");
        // Treat a click as an implicit "thanks, you've answered" — hide
        // the review block so we don't ask again on refresh.
        markDismissedLocally(ctx.project.retailer.id, ctx.project.customer.id);
        ctx.showToast("Thank you — every review helps a small business.", "success");
        hideReviewBlock();
        return;
      }

      const dismissBtn = target.closest<HTMLButtonElement>(
        "[data-role='review-dismiss']",
      );
      if (dismissBtn) {
        markDismissedLocally(ctx.project.retailer.id, ctx.project.customer.id);
        reportEvent("dismissed");
        hideReviewBlock();
        return;
      }

      // ── Referral block ────────────────────────────────────────────
      const copyBtn = target.closest<HTMLButtonElement>("[data-role='referral-copy']");
      if (copyBtn) {
        const linkInput = container.querySelector<HTMLInputElement>(
          "[data-role='referral-link']",
        );
        const link = linkInput?.value ?? "";
        if (!link) return;
        void (async () => {
          const ok = await copyText(link);
          if (ok) {
            ctx.showToast("Referral link copied — paste it anywhere.", "success");
          } else {
            ctx.showToast("Couldn't copy. Select the link and copy manually.", "error");
            linkInput?.select();
          }
        })();
        return;
      }

      const sendBtn = target.closest<HTMLButtonElement>("[data-role='referral-send']");
      if (sendBtn) {
        // Platform-only — switch to the in-portal Messages tab so the
        // customer can share the link there. NO mailto:/sms:. Dispatches
        // the shell's `cust-nav` CustomEvent (shell/nav.ts).
        window.dispatchEvent(
          new CustomEvent("cust-nav", { detail: { page: "messages" } }),
        );
        ctx.showToast("Drop your referral link into a message.", "info");
        return;
      }
    }

    container.addEventListener("click", onClick);

    return () => {
      ac.abort();
      container.removeEventListener("click", onClick);
      container.replaceChildren();
    };
  },
};

export default section;
